type Json = Record<string, unknown>;

const DAY_MS = 86_400_000;
const MAX_CUSTOM_RANGE_DAYS = 3660;
const CREDIT_RANGE_MODEL_KEYS = [
  "as_of_date",
  "kind",
  "schema_version",
] as const;

type ExactDecimal = {
  units: bigint;
  scale: number;
};

type ParsedDocumentAmount = {
  currency: string | null;
  documentCount: number;
  missingAmountCount: number;
  totalAmount: ExactDecimal | null;
};

type ParsedConfirmedAmount = {
  currency: string | null;
  linkCount: number;
  creditDocumentCount: number;
  allocationAmount: ExactDecimal;
};

type ParsedPeriod = {
  raw: Json;
  from: number;
  to: number;
  documentAmounts: Map<string, ParsedDocumentAmount>;
  confirmedAmounts: Map<string, ParsedConfirmedAmount> | null;
};

type DocumentSegmentAmount = ParsedDocumentAmount & {
  totalAmount: ExactDecimal | null;
};

type ConfirmedSegmentAmount = ParsedConfirmedAmount;

type SyntheticDay = {
  documents: Map<
    string,
    {
      currency: string | null;
      documentCount: number;
      missingAmountCount: number;
      presentDocumentCount: number;
      totalAmountUnits: bigint;
      amountScale: number;
    }
  >;
  confirmed: Map<
    string,
    {
      currency: string | null;
      linkCount: number;
      creditDocumentCount: number;
      allocationUnits: bigint;
      amountScale: number;
    }
  > | null;
};

export class SyntheticCreditRangeError extends Error {}

/**
 * Builds a deterministic Demo-only period from the already published 1m/1y
 * aggregates. The model deliberately contains no dates or transaction-level
 * values beyond its public anchor date.
 */
export function synthesizeCreditRange(
  credits: Json,
  modelValue: unknown,
  fromDate: string,
  toDate: string,
): Json {
  const asOfDate = stringField(credits, "as_of_date");
  validateModel(modelValue, asOfDate);
  const asOfDay = requiredIsoDay(asOfDate, "Standdatum");
  const fromDay = requiredIsoDay(fromDate, "Von-Datum");
  const toDay = requiredIsoDay(toDate, "Bis-Datum");
  if (
    fromDay > toDay ||
    toDay > asOfDay ||
    dayDistance(fromDay, toDay) + 1 > MAX_CUSTOM_RANGE_DAYS
  ) {
    fail("Demo: eigener Gutschriften-Zeitraum ist nicht zulässig.");
  }

  const periods = recordField(credits, "periods");
  const oneMonth = parsePeriod(recordField(periods, "1m"), "1m");
  const oneYear = parsePeriod(recordField(periods, "1y"), "1y");
  validateAnchors(credits, oneMonth, oneYear, asOfDay);
  const earlierDocuments = subtractDocumentAmounts(
    oneYear.documentAmounts,
    oneMonth.documentAmounts,
  );
  const earlierConfirmed = subtractConfirmedAmounts(
    oneYear.confirmedAmounts,
    oneMonth.confirmedAmounts,
  );
  validateConfirmedDocumentCapacity(earlierDocuments, earlierConfirmed);

  // Preserve the published anchors byte-for-byte at their exact boundaries.
  if (fromDate === isoDate(oneMonth.from) && toDate === isoDate(oneMonth.to)) {
    return oneMonth.raw;
  }
  if (fromDate === isoDate(oneYear.from) && toDate === isoDate(oneYear.to)) {
    return oneYear.raw;
  }

  const cycleLength = dayDistance(oneYear.from, oneYear.to) + 1;
  const monthOffset = dayDistance(oneYear.from, oneMonth.from);
  const earlierLength = monthOffset;
  const monthLength = cycleLength - monthOffset;

  const schedule = [
    ...buildSegmentDays(
      earlierLength,
      earlierDocuments,
      earlierConfirmed,
      "year-before-month",
    ),
    ...buildSegmentDays(
      monthLength,
      [...oneMonth.documentAmounts.values()],
      oneMonth.confirmedAmounts === null
        ? null
        : [...oneMonth.confirmedAmounts.values()],
      "month-anchor",
    ),
  ];
  if (schedule.length !== cycleLength) {
    fail("Demo: synthetischer Gutschriften-Zyklus ist nicht lesbar.");
  }

  return aggregateRange(
    schedule,
    oneYear.from,
    fromDay,
    toDay,
    fromDate,
    toDate,
  );
}

function validateModel(value: unknown, asOfDate: string): void {
  if (!isJson(value)) {
    fail("Demo: synthetisches Gutschriften-Modell ist nicht verfügbar.");
  }
  const keys = Object.keys(value).sort();
  if (
    keys.length !== CREDIT_RANGE_MODEL_KEYS.length ||
    CREDIT_RANGE_MODEL_KEYS.some((key, index) => key !== keys[index]) ||
    value.schema_version !== 1 ||
    value.kind !== "synthetic_anchor_repeat" ||
    value.as_of_date !== asOfDate
  ) {
    fail("Demo: synthetisches Gutschriften-Modell ist nicht lesbar.");
  }
}

function validateAnchors(
  credits: Json,
  month: ParsedPeriod,
  year: ParsedPeriod,
  asOfDay: number,
): void {
  const monthDays = dayDistance(month.from, month.to) + 1;
  const yearDays = dayDistance(year.from, year.to) + 1;
  if (
    month.to !== asOfDay ||
    year.to !== asOfDay ||
    year.from >= month.from ||
    month.from > month.to ||
    monthDays < 29 ||
    monthDays > 32 ||
    yearDays < 366 ||
    yearDays > 367
  ) {
    fail("Demo: Gutschriften-Ankerzeiträume sind nicht lesbar.");
  }
  const matching = recordField(credits, "matching");
  const status = stringField(matching, "status");
  if (
    status !== "active" &&
    status !== "not_activated" &&
    status !== "data_quality_issue"
  ) {
    fail("Demo: Gutschriften-Zuordnungsstatus ist nicht lesbar.");
  }
  const matchingAvailable = month.confirmedAmounts !== null;
  if (
    (status === "active" && !matchingAvailable) ||
    (status !== "active" && matchingAvailable) ||
    (year.confirmedAmounts !== null) !== matchingAvailable
  ) {
    fail("Demo: Gutschriften-Zuordnung widerspricht dem Datenstand.");
  }
}

function parsePeriod(value: Json, label: string): ParsedPeriod {
  const fromDate = stringField(value, "from_date");
  const toDate = stringField(value, "to_date");
  const from = requiredIsoDay(fromDate, `${label}-Von-Datum`);
  const to = requiredIsoDay(toDate, `${label}-Bis-Datum`);
  if (from > to) {
    fail(`Demo: ${label}-Zeitraum ist nicht lesbar.`);
  }

  const documents = recordField(value, "credit_documents");
  const documentCount = integerField(documents, "document_count");
  const documentAmounts = new Map<string, ParsedDocumentAmount>();
  let amountDocumentCount = 0;
  for (const rowValue of arrayField(documents, "amounts")) {
    if (!isJson(rowValue)) {
      fail("Demo: Gutschriften-Betrag ist nicht lesbar.");
    }
    const currency = currencyField(rowValue);
    const key = currencyKey(currency);
    if (documentAmounts.has(key)) {
      fail("Demo: Gutschriften-Währungen sind nicht eindeutig.");
    }
    const rowDocumentCount = positiveIntegerField(
      rowValue,
      "document_count",
    );
    const missingAmountCount = integerField(
      rowValue,
      "missing_amount_count",
    );
    if (missingAmountCount > rowDocumentCount) {
      fail("Demo: fehlende Gutschriften-Beträge sind widersprüchlich.");
    }
    const presentDocumentCount = rowDocumentCount - missingAmountCount;
    const totalAmount = decimalField(rowValue, "total_amount", true);
    if (
      (presentDocumentCount === 0 && totalAmount !== null) ||
      (presentDocumentCount > 0 && totalAmount === null)
    ) {
      fail("Demo: fehlende Gutschriften-Beträge sind widersprüchlich.");
    }
    amountDocumentCount = safeAdd(amountDocumentCount, rowDocumentCount);
    documentAmounts.set(key, {
      currency,
      documentCount: rowDocumentCount,
      missingAmountCount,
      totalAmount,
    });
  }
  if (amountDocumentCount !== documentCount) {
    fail("Demo: Gutschriften-Anzahlen sind widersprüchlich.");
  }

  const confirmedValue = value.confirmed_links;
  const confirmedAmounts =
    confirmedValue === null ? null : parseConfirmed(record(confirmedValue));
  if (confirmedAmounts !== null) {
    for (const [key, confirmedAmount] of confirmedAmounts) {
      const documentAmount = documentAmounts.get(key);
      if (
        !documentAmount ||
        confirmedAmount.creditDocumentCount > documentAmount.documentCount
      ) {
        fail("Demo: Zuordnungs-Währungen sind widersprüchlich.");
      }
    }
  }
  return {
    raw: value,
    from,
    to,
    documentAmounts,
    confirmedAmounts,
  };
}

function parseConfirmed(value: Json): Map<string, ParsedConfirmedAmount> {
  const linkCount = integerField(value, "link_count");
  const creditDocumentCount = integerField(value, "credit_document_count");
  if (creditDocumentCount > linkCount) {
    fail("Demo: bestätigte Gutschriften-Zuordnungen sind widersprüchlich.");
  }
  const amounts = new Map<string, ParsedConfirmedAmount>();
  let amountLinkCount = 0;
  let amountDocumentCount = 0;
  for (const rowValue of arrayField(value, "amounts")) {
    if (!isJson(rowValue)) {
      fail("Demo: Zuordnungsbetrag ist nicht lesbar.");
    }
    const currency = currencyField(rowValue);
    const key = currencyKey(currency);
    if (amounts.has(key)) {
      fail("Demo: Zuordnungs-Währungen sind nicht eindeutig.");
    }
    const rowLinkCount = positiveIntegerField(rowValue, "link_count");
    const rowDocumentCount = positiveIntegerField(
      rowValue,
      "credit_document_count",
    );
    if (rowDocumentCount > rowLinkCount) {
      fail("Demo: bestätigte Gutschriften-Zuordnungen sind widersprüchlich.");
    }
    const allocationAmount = decimalField(
      rowValue,
      "allocation_amount",
      false,
    );
    if (allocationAmount === null || allocationAmount.units < 0n) {
      fail("Demo: Zuordnungsbetrag ist nicht lesbar.");
    }
    amountLinkCount = safeAdd(amountLinkCount, rowLinkCount);
    amountDocumentCount = safeAdd(
      amountDocumentCount,
      rowDocumentCount,
    );
    amounts.set(key, {
      currency,
      linkCount: rowLinkCount,
      creditDocumentCount: rowDocumentCount,
      allocationAmount,
    });
  }
  if (
    amountLinkCount !== linkCount ||
    amountDocumentCount !== creditDocumentCount
  ) {
    fail("Demo: bestätigte Gutschriften-Zuordnungen sind widersprüchlich.");
  }
  return amounts;
}

function subtractDocumentAmounts(
  year: Map<string, ParsedDocumentAmount>,
  month: Map<string, ParsedDocumentAmount>,
): DocumentSegmentAmount[] {
  const result: DocumentSegmentAmount[] = [];
  for (const key of sortedCurrencyKeys(year, month)) {
    const yearAmount = year.get(key);
    const monthAmount = month.get(key);
    const currency = yearAmount?.currency ?? monthAmount?.currency ?? null;
    const documentCount = subtractCount(
      yearAmount?.documentCount ?? 0,
      monthAmount?.documentCount ?? 0,
      "Gutschriften-Anzahl",
    );
    const missingAmountCount = subtractCount(
      yearAmount?.missingAmountCount ?? 0,
      monthAmount?.missingAmountCount ?? 0,
      "Anzahl fehlender Gutschriften-Beträge",
    );
    if (missingAmountCount > documentCount) {
      fail("Demo: fehlende Gutschriften-Beträge sind widersprüchlich.");
    }
    const presentDocumentCount = documentCount - missingAmountCount;
    const yearTotal = yearAmount?.totalAmount ?? zeroDecimal(monthAmount);
    const monthTotal = monthAmount?.totalAmount ?? zeroDecimal(yearAmount);
    const totalDifference = subtractDecimal(yearTotal, monthTotal);
    if (presentDocumentCount === 0 && totalDifference.units !== 0n) {
      fail("Demo: Gutschriften-Beträge sind nicht teilbar.");
    }
    if (documentCount > 0) {
      result.push({
        currency,
        documentCount,
        missingAmountCount,
        totalAmount: presentDocumentCount === 0 ? null : totalDifference,
      });
    }
  }
  return result;
}

function subtractConfirmedAmounts(
  year: Map<string, ParsedConfirmedAmount> | null,
  month: Map<string, ParsedConfirmedAmount> | null,
): ConfirmedSegmentAmount[] | null {
  if (year === null || month === null) {
    if (year !== month) {
      fail("Demo: Gutschriften-Zuordnung widerspricht dem Datenstand.");
    }
    return null;
  }
  const result: ConfirmedSegmentAmount[] = [];
  for (const key of sortedCurrencyKeys(year, month)) {
    const yearAmount = year.get(key);
    const monthAmount = month.get(key);
    const currency = yearAmount?.currency ?? monthAmount?.currency ?? null;
    const linkCount = subtractCount(
      yearAmount?.linkCount ?? 0,
      monthAmount?.linkCount ?? 0,
      "Zuordnungs-Anzahl",
    );
    const creditDocumentCount = subtractCount(
      yearAmount?.creditDocumentCount ?? 0,
      monthAmount?.creditDocumentCount ?? 0,
      "Anzahl zugeordneter Gutschrift-Belege",
    );
    if (creditDocumentCount > linkCount) {
      fail("Demo: bestätigte Gutschriften-Zuordnungen sind widersprüchlich.");
    }
    const yearAllocation =
      yearAmount?.allocationAmount ?? zeroDecimal(monthAmount);
    const monthAllocation =
      monthAmount?.allocationAmount ?? zeroDecimal(yearAmount);
    const allocationDifference = subtractDecimal(
      yearAllocation,
      monthAllocation,
    );
    if (
      allocationDifference.units < 0n ||
      (linkCount === 0 && allocationDifference.units !== 0n) ||
      (linkCount > 0 && creditDocumentCount === 0)
    ) {
      fail("Demo: Zuordnungsbeträge sind nicht teilbar.");
    }
    if (linkCount > 0) {
      result.push({
        currency,
        linkCount,
        creditDocumentCount,
        allocationAmount: allocationDifference,
      });
    }
  }
  return result;
}

function validateConfirmedDocumentCapacity(
  documents: DocumentSegmentAmount[],
  confirmed: ConfirmedSegmentAmount[] | null,
): void {
  if (confirmed === null) return;
  const documentsByCurrency = new Map(
    documents.map((amount) => [currencyKey(amount.currency), amount]),
  );
  for (const amount of confirmed) {
    const documentAmount = documentsByCurrency.get(currencyKey(amount.currency));
    if (
      !documentAmount ||
      amount.creditDocumentCount > documentAmount.documentCount
    ) {
      fail("Demo: bestätigte Gutschriften-Zuordnungen sind nicht teilbar.");
    }
  }
}

function buildSegmentDays(
  length: number,
  documentAmounts: DocumentSegmentAmount[],
  confirmedAmounts: ConfirmedSegmentAmount[] | null,
  segmentName: string,
): SyntheticDay[] {
  if (!Number.isSafeInteger(length) || length <= 0) {
    fail("Demo: Gutschriften-Ankerzeiträume sind nicht teilbar.");
  }
  const days: SyntheticDay[] = Array.from({ length }, () => ({
    documents: new Map(),
    confirmed: confirmedAmounts === null ? null : new Map(),
  }));

  for (const amount of documentAmounts) {
    const salt = `${segmentName}:documents:${amount.currency ?? "none"}`;
    const documentCounts = distributeCount(amount.documentCount, length, salt);
    const missingCounts = distributeCount(
      amount.missingAmountCount,
      length,
      salt,
    );
    const presentCounts = documentCounts.map(
      (count, index) => count - missingCounts[index],
    );
    if (presentCounts.some((count) => count < 0)) {
      fail("Demo: fehlende Gutschriften-Beträge sind nicht teilbar.");
    }
    const scale = amount.totalAmount?.scale ?? 0;
    const amountUnits = distributeUnits(
      amount.totalAmount?.units ?? 0n,
      presentCounts,
      `${salt}:amount`,
    );
    const key = currencyKey(amount.currency);
    for (let index = 0; index < length; index += 1) {
      if (documentCounts[index] === 0) continue;
      days[index].documents.set(key, {
        currency: amount.currency,
        documentCount: documentCounts[index],
        missingAmountCount: missingCounts[index],
        presentDocumentCount: presentCounts[index],
        totalAmountUnits: amountUnits[index],
        amountScale: scale,
      });
    }
  }

  if (confirmedAmounts !== null) {
    for (const amount of confirmedAmounts) {
      const salt = `${segmentName}:confirmed:${amount.currency ?? "none"}`;
      const key = currencyKey(amount.currency);
      const documentCapacities = days.map(
        (day) => day.documents.get(key)?.documentCount ?? 0,
      );
      const documentCounts = distributeWithinCapacities(
        amount.creditDocumentCount,
        documentCapacities,
        `${salt}:documents`,
      );
      const activeDays = documentCounts
        .map((count, index) => (count > 0 ? index : -1))
        .filter((index) => index >= 0);
      if (activeDays.length === 0) {
        fail("Demo: bestätigte Gutschriften-Zuordnungen sind nicht teilbar.");
      }
      const linkCounts = [...documentCounts];
      const extraLinks = amount.linkCount - amount.creditDocumentCount;
      const extraLinkCounts = distributeCount(
        extraLinks,
        activeDays.length,
        `${salt}:extra-links`,
      );
      activeDays.forEach((dayIndex, index) => {
        linkCounts[dayIndex] = safeAdd(
          linkCounts[dayIndex],
          extraLinkCounts[index],
        );
      });
      const amountUnits = distributeUnits(
        amount.allocationAmount.units,
        linkCounts,
        `${salt}:amount`,
      );
      for (let index = 0; index < length; index += 1) {
        if (linkCounts[index] === 0) continue;
        days[index].confirmed?.set(key, {
          currency: amount.currency,
          linkCount: linkCounts[index],
          creditDocumentCount: documentCounts[index],
          allocationUnits: amountUnits[index],
          amountScale: amount.allocationAmount.scale,
        });
      }
    }
  }
  return days;
}

function distributeWithinCapacities(
  total: number,
  capacities: number[],
  salt: string,
): number[] {
  const capacity = capacities.reduce((sum, value) => safeAdd(sum, value), 0);
  if (total > capacity) {
    fail("Demo: bestätigte Gutschriften-Zuordnungen sind nicht teilbar.");
  }
  const values = capacities.map(() => 0);
  const order = deterministicOrder(capacities.length, salt);
  let remaining = total;
  while (remaining > 0) {
    const available = order.filter(
      (index) => values[index] < capacities[index],
    );
    if (available.length === 0) {
      fail("Demo: bestätigte Gutschriften-Zuordnungen sind nicht teilbar.");
    }
    const evenShare = Math.floor(remaining / available.length);
    if (evenShare === 0) {
      for (let index = 0; index < remaining; index += 1) {
        values[available[index]] += 1;
      }
      remaining = 0;
      break;
    }
    let allocated = 0;
    for (const index of available) {
      const amount = Math.min(
        evenShare,
        capacities[index] - values[index],
      );
      values[index] = safeAdd(values[index], amount);
      allocated = safeAdd(allocated, amount);
    }
    if (allocated === 0) {
      fail("Demo: bestätigte Gutschriften-Zuordnungen sind nicht teilbar.");
    }
    remaining -= allocated;
  }
  return values;
}

function aggregateRange(
  schedule: SyntheticDay[],
  cycleStart: number,
  from: number,
  to: number,
  fromDate: string,
  toDate: string,
): Json {
  const documents = new Map<
    string,
    {
      currency: string | null;
      documentCount: number;
      missingAmountCount: number;
      presentDocumentCount: number;
      totalAmount: ExactDecimal;
    }
  >();
  const confirmed = schedule[0].confirmed === null
    ? null
    : new Map<
        string,
        {
          currency: string | null;
          linkCount: number;
          creditDocumentCount: number;
          allocationAmount: ExactDecimal;
        }
      >();

  for (let day = from; day <= to; day += DAY_MS) {
    const offset = dayDistance(cycleStart, day);
    const cycleIndex = modulo(offset, schedule.length);
    const syntheticDay = schedule[cycleIndex];
    for (const [key, value] of syntheticDay.documents) {
      const bucket = documents.get(key) ?? {
        currency: value.currency,
        documentCount: 0,
        missingAmountCount: 0,
        presentDocumentCount: 0,
        totalAmount: { units: 0n, scale: value.amountScale },
      };
      bucket.documentCount = safeAdd(
        bucket.documentCount,
        value.documentCount,
      );
      bucket.missingAmountCount = safeAdd(
        bucket.missingAmountCount,
        value.missingAmountCount,
      );
      bucket.presentDocumentCount = safeAdd(
        bucket.presentDocumentCount,
        value.presentDocumentCount,
      );
      bucket.totalAmount = addDecimal(bucket.totalAmount, {
        units: value.totalAmountUnits,
        scale: value.amountScale,
      });
      documents.set(key, bucket);
    }
    if (confirmed !== null && syntheticDay.confirmed !== null) {
      for (const [key, value] of syntheticDay.confirmed) {
        const bucket = confirmed.get(key) ?? {
          currency: value.currency,
          linkCount: 0,
          creditDocumentCount: 0,
          allocationAmount: { units: 0n, scale: value.amountScale },
        };
        bucket.linkCount = safeAdd(bucket.linkCount, value.linkCount);
        bucket.creditDocumentCount = safeAdd(
          bucket.creditDocumentCount,
          value.creditDocumentCount,
        );
        bucket.allocationAmount = addDecimal(bucket.allocationAmount, {
          units: value.allocationUnits,
          scale: value.amountScale,
        });
        confirmed.set(key, bucket);
      }
    }
  }

  const documentRows = sortByCurrency([...documents.values()]).map(
    (bucket) => ({
      currency: bucket.currency,
      document_count: bucket.documentCount,
      total_amount:
        bucket.presentDocumentCount === 0
          ? null
          : formatExactDecimal(bucket.totalAmount),
      missing_amount_count: bucket.missingAmountCount,
    }),
  );
  const confirmedRows =
    confirmed === null
      ? null
      : sortByCurrency([...confirmed.values()]).map((bucket) => ({
          currency: bucket.currency,
          link_count: bucket.linkCount,
          credit_document_count: bucket.creditDocumentCount,
          allocation_amount: formatExactDecimal(bucket.allocationAmount),
        }));

  return {
    from_date: fromDate,
    to_date: toDate,
    credit_documents: {
      document_count: documentRows.reduce(
        (sum, row) => safeAdd(sum, row.document_count),
        0,
      ),
      amounts: documentRows,
    },
    confirmed_links:
      confirmedRows === null
        ? null
        : {
            link_count: confirmedRows.reduce(
              (sum, row) => safeAdd(sum, row.link_count),
              0,
            ),
            credit_document_count: confirmedRows.reduce(
              (sum, row) => safeAdd(sum, row.credit_document_count),
              0,
            ),
            amounts: confirmedRows,
          },
  };
}

function distributeCount(total: number, length: number, salt: string): number[] {
  const base = Math.floor(total / length);
  const remainder = total % length;
  const values = Array.from({ length }, () => base);
  const order = deterministicOrder(length, salt);
  for (let index = 0; index < remainder; index += 1) {
    values[order[index]] += 1;
  }
  return values;
}

function distributeUnits(
  total: bigint,
  capacities: number[],
  salt: string,
): bigint[] {
  const capacity = capacities.reduce((sum, value) => safeAdd(sum, value), 0);
  if (capacity === 0) {
    if (total !== 0n) {
      fail("Demo: Gutschriften-Betrag ist nicht teilbar.");
    }
    return capacities.map(() => 0n);
  }
  const divisor = BigInt(capacity);
  const base = total / divisor;
  let remainder = total % divisor;
  const values = capacities.map((value) => base * BigInt(value));
  const direction = remainder < 0n ? -1n : 1n;
  if (remainder < 0n) remainder = -remainder;
  for (const dayIndex of deterministicOrder(capacities.length, salt)) {
    if (remainder === 0n) break;
    const assigned =
      remainder < BigInt(capacities[dayIndex])
        ? remainder
        : BigInt(capacities[dayIndex]);
    values[dayIndex] += direction * assigned;
    remainder -= assigned;
  }
  if (remainder !== 0n) {
    fail("Demo: Gutschriften-Betrag ist nicht teilbar.");
  }
  return values;
}

function deterministicOrder(length: number, salt: string): number[] {
  const hash = stableHash(salt);
  const start = hash % length;
  let step = ((hash >>> 8) % length) || 1;
  while (greatestCommonDivisor(step, length) !== 1) {
    step = (step + 1) % length || 1;
  }
  return Array.from({ length }, (_, index) => (start + index * step) % length);
}

function stableHash(value: string): number {
  let hash = 2_166_136_261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16_777_619);
  }
  return hash >>> 0;
}

function greatestCommonDivisor(left: number, right: number): number {
  let a = left;
  let b = right;
  while (b !== 0) {
    const remainder = a % b;
    a = b;
    b = remainder;
  }
  return a;
}

function sortedCurrencyKeys<T extends { currency: string | null }>(
  left: Map<string, T>,
  right: Map<string, T>,
): string[] {
  return [...new Set([...left.keys(), ...right.keys()])].sort((a, b) => {
    const leftCurrency = left.get(a)?.currency ?? right.get(a)?.currency ?? null;
    const rightCurrency = left.get(b)?.currency ?? right.get(b)?.currency ?? null;
    return compareCurrency(leftCurrency, rightCurrency);
  });
}

function sortByCurrency<T extends { currency: string | null }>(values: T[]): T[] {
  return values.sort((left, right) =>
    compareCurrency(left.currency, right.currency),
  );
}

function compareCurrency(left: string | null, right: string | null): number {
  if (left === null) return right === null ? 0 : 1;
  if (right === null) return -1;
  return left.localeCompare(right);
}

function currencyKey(currency: string | null): string {
  return currency === null ? "null" : `value:${currency}`;
}

function currencyField(value: Json): string | null {
  const currency = value.currency;
  if (currency === null) return null;
  if (
    typeof currency !== "string" ||
    currency.length === 0 ||
    currency.length > 8 ||
    /[\u0000-\u001f]/.test(currency)
  ) {
    fail("Demo: Währung ist nicht lesbar.");
  }
  return currency;
}

function decimalField(
  value: Json,
  field: string,
  nullable: boolean,
): ExactDecimal | null {
  const nested = value[field];
  if (nested === null && nullable) return null;
  const text =
    typeof nested === "string"
      ? nested
      : typeof nested === "number" && Number.isFinite(nested)
        ? String(nested)
        : "";
  const match = /^(-?)(\d+)(?:\.(\d{1,24}))?$/.exec(text);
  if (!match) {
    fail(`Demo: ${field} ist nicht lesbar.`);
  }
  const fraction = match[3] ?? "";
  return {
    units: BigInt(`${match[1]}${match[2]}${fraction}`),
    scale: fraction.length,
  };
}

function zeroDecimal(
  value:
    | ParsedDocumentAmount
    | ParsedConfirmedAmount
    | undefined,
): ExactDecimal {
  const amount =
    value && "totalAmount" in value
      ? value.totalAmount
      : value && "allocationAmount" in value
        ? value.allocationAmount
        : null;
  return { units: 0n, scale: amount?.scale ?? 0 };
}

function subtractDecimal(left: ExactDecimal, right: ExactDecimal): ExactDecimal {
  const scale = Math.max(left.scale, right.scale);
  return {
    units:
      left.units * 10n ** BigInt(scale - left.scale) -
      right.units * 10n ** BigInt(scale - right.scale),
    scale,
  };
}

function addDecimal(left: ExactDecimal, right: ExactDecimal): ExactDecimal {
  const scale = Math.max(left.scale, right.scale);
  return {
    units:
      left.units * 10n ** BigInt(scale - left.scale) +
      right.units * 10n ** BigInt(scale - right.scale),
    scale,
  };
}

function formatExactDecimal(value: ExactDecimal): string {
  const negative = value.units < 0n;
  const digits = (negative ? -value.units : value.units)
    .toString()
    .padStart(value.scale + 1, "0");
  if (value.scale === 0) return `${negative ? "-" : ""}${digits}`;
  const split = digits.length - value.scale;
  return `${negative ? "-" : ""}${digits.slice(0, split)}.${digits.slice(
    split,
  )}`;
}

function subtractCount(year: number, month: number, label: string): number {
  if (month > year) {
    fail(`Demo: ${label} ist nicht teilbar.`);
  }
  return year - month;
}

function positiveIntegerField(value: Json, field: string): number {
  const parsed = integerField(value, field);
  if (parsed === 0) fail(`Demo: ${field} ist nicht lesbar.`);
  return parsed;
}

function integerField(value: Json, field: string): number {
  const nested = value[field];
  const parsed =
    typeof nested === "number"
      ? nested
      : typeof nested === "string" && /^\d+$/.test(nested)
        ? Number(nested)
        : Number.NaN;
  if (!Number.isSafeInteger(parsed) || parsed < 0) {
    fail(`Demo: ${field} ist nicht lesbar.`);
  }
  return parsed;
}

function recordField(value: Json, field: string): Json {
  return record(value[field], field);
}

function record(value: unknown, field = "Wert"): Json {
  if (!isJson(value)) {
    fail(`Demo: ${field} ist nicht lesbar.`);
  }
  return value;
}

function arrayField(value: Json, field: string): unknown[] {
  const nested = value[field];
  if (!Array.isArray(nested)) {
    fail(`Demo: ${field} ist nicht lesbar.`);
  }
  return nested;
}

function stringField(value: Json, field: string): string {
  const nested = value[field];
  if (typeof nested !== "string") {
    fail(`Demo: ${field} ist nicht lesbar.`);
  }
  return nested;
}

function requiredIsoDay(value: string, label: string): number {
  const parsed = isoDateDay(value);
  if (parsed === null) fail(`Demo: ${label} ist nicht lesbar.`);
  return parsed;
}

function isoDateDay(value: string): number | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const timestamp = Date.UTC(year, month - 1, day);
  const parsed = new Date(timestamp);
  return parsed.getUTCFullYear() === year &&
    parsed.getUTCMonth() === month - 1 &&
    parsed.getUTCDate() === day
    ? timestamp
    : null;
}

function isoDate(timestamp: number): string {
  return new Date(timestamp).toISOString().slice(0, 10);
}

function dayDistance(from: number, to: number): number {
  return Math.round((to - from) / DAY_MS);
}

function modulo(value: number, divisor: number): number {
  return ((value % divisor) + divisor) % divisor;
}

function safeAdd(left: number, right: number): number {
  const value = left + right;
  if (!Number.isSafeInteger(value)) {
    fail("Demo: Gutschriften-Anzahl ist zu groß.");
  }
  return value;
}

function isJson(value: unknown): value is Json {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function fail(message: string): never {
  throw new SyntheticCreditRangeError(message);
}
